import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../axiosInstance";

// usage 

// get
// const { data, isLoading, error } = useAxios("/v2/user/profile");

// post 
// const { data, refetch, isLoading } = useAxios(
//   "/v2/job/create",
//   {
//     method: "post",
//     data: { title: "Frontend Engineer", company: "Northstarz" }
//   },
//   false // don’t run immediately
// );
// <button onClick={refetch}>Create Job</button>

// patch
// const { refetch } = useAxios(
//   "/v2/job/update/123",
//   {
//     method: "patch",
//     data: { status: "closed" }
//   },
//   false
// );

// useEffect(() => {
//   refetch();
// }, [refetch]);

const useFetch = (url, options = {}, immediate = true) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(immediate);
  const [error, setError] = useState([]);

  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const fetchData = useCallback(async () => {
    if (!url) return [];

    setIsLoading(true);
    setError([]);

    try {
      const response = await axiosInstance({
        url,
        ...optionsRef.current,
      });
      setData(response.data);
      return response.data;
    } catch (err) {
      setData([]);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  // ⭐⭐⭐ NEW: send() that accepts a body and performs request ⭐⭐⭐
  const send = useCallback(
    async (body = {}) => {
      if (!url) return [];

      setIsLoading(true);
      setError([]);

      try {
        const response = await axiosInstance({
          url,
          ...optionsRef.current,
          data: body, // override body
        });

        setData(response.data);
        return response.data;
      } catch (err) {
        setData([]);
        setError(err);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [url]
  );

  useEffect(() => {
    if (immediate && url) {
      fetchData();
    }
  }, [fetchData, immediate, url]);

  // ⬇️ RETURN send()
  return { data, isLoading, error, refetch: fetchData, send };
};

export default useFetch;
